import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Email,
  CheckCircle,
  Pets,
  Security,
  Warning,
  LocalFlorist,
  Memory,
  Shield,
  Speed,
  CameraAlt,
  Router,
  DeviceHub,
  Launch,
  Login as LoginIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PublicNavBar from './PublicNavBar';
import PublicFooter from './PublicFooter';
import { trackBetaInterest } from '../analytics';

const BetaTestersPage = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();

  // Set page title for beta program
  useEffect(() => {
    document.title = 'EyeSense.ai Beta Program - Join Early Testing';
    
    // Cleanup: reset title when component unmounts
    return () => {
      document.title = 'EyeSense.ai - Intelligent Edge AI Monitoring';
    };
  }, []);

  const betaTestFeatures = [
    {
      title: "Hardware Platform Guidance",
      description: "The YOLO deer model fits a smaller computer when you skip the large vision model. A GPU machine is optional, for checks or fine-tuning. You buy the hardware.",
      icon: <Memory />
    },
    {
      title: "Direct Development Feedback",
      description: "Direct channel to our development team for feature requests and bug reports",
      icon: <Router />
    },
    {
      title: "Early AI Model Access",
      description: "First access to new detection models and expanding AI capabilities",
      icon: <Security />
    },
    {
      title: "Use Case Validation",
      description: "Help validate new detection models and real-world use cases",
      icon: <CheckCircle />
    }
  ];

  const idealCandidates = [
    "Property owners dealing with deer, cats, or wildlife intrusion issues",
    "People who can install software on a PC they supply, with or without a GPU",
    "Security-conscious individuals wanting local-only monitoring solutions",
    "Anyone with existing Rachio or smart irrigation systems",
    "Developers interested in expanding AI capabilities for new use cases",
    "Tech enthusiasts passionate about privacy-first AI solutions"
  ];

  const provenCapabilities = [
    {
      category: "Wildlife Deterrent",
      status: "✅ Currently Validated",
      description: "Successfully tested with deer and cat detection/deterrence",
      icon: <Pets sx={{ color: theme.palette.success.main }} />
    },
    {
      category: "Security Monitoring", 
      status: "✅ Currently Validated",
      description: "Person and vehicle detection with automated alerts",
      icon: <Security sx={{ color: theme.palette.success.main }} />
    },
    {
      category: "Wildfire Detection",
      status: "🔮 Future Potential",
      description: "Early smoke and fire detection capabilities (in development)",
      icon: <Warning sx={{ color: theme.palette.warning.main }} />
    },
    {
      category: "Advanced Garden Protection",
      status: "🔮 Future Potential", 
      description: "Smart irrigation and comprehensive pest management",
      icon: <LocalFlorist sx={{ color: theme.palette.warning.main }} />
    }
  ];

  const handleEmailContact = () => {
    trackBetaInterest('mailto');
    window.location.href = 'mailto:info@pomsoft.net?subject=EyeSense.ai Beta Testing Interest&body=Hi,%0D%0A%0D%0AI am interested in joining the EyeSense.ai early beta testing program.%0D%0A%0D%0APlease provide me with enrollment details and next steps.%0D%0A%0D%0AThank you!';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Helmet>
        <title>EyeSense.ai Beta Program - Join Early Testing</title>
        <meta name="description" content="Join the EyeSense.ai beta. Deer detection runs on a small model with no GPU. A GPU is only for large-vision checks or fine-tuning. Wildfire detection is in development. You supply the computer." />
        <meta property="og:title" content="EyeSense.ai Beta Program - Join Early Testing" />
        <meta property="og:description" content="Deer detection runs without a GPU. A GPU is only for large-vision checks or fine-tuning. Wildfire detection is in development. You supply the computer." />
        <meta property="og:type" content="website" />
        <meta name="twitter:title" content="EyeSense.ai Beta Program - Join Early Testing" />
        <meta name="twitter:description" content="Deer detection runs without a GPU. Wildfire detection is in development. You install the software and supply the computer." />
      </Helmet>
      {/* Top Navigation Bar */}
      <PublicNavBar />

      {/* Hero Section */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
          py: 8,
          pt: 12, // Extra padding for fixed AppBar
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              variant="h2"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 3
              }}
            >
              Join the EyeSense.ai Beta Program
            </Typography>
            <Typography variant="h5" color="text.secondary" paragraph sx={{ mb: 4, maxWidth: 800, mx: 'auto' }}>
              Be among the first to experience the future of intelligent edge AI monitoring. 
              Help us validate and improve our revolutionary gateway technology.
            </Typography>
            <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap>
              <Chip 
                icon={<Memory />} 
                label="Edge AI Processing" 
                variant="outlined" 
                sx={{ mb: 1 }}
              />
              <Chip 
                icon={<Shield />} 
                label="Privacy First" 
                variant="outlined" 
                sx={{ mb: 1 }}
              />
              <Chip 
                icon={<Speed />} 
                label="Real-time Response" 
                variant="outlined" 
                sx={{ mb: 1 }}
              />
              <Chip 
                icon={<CameraAlt />} 
                label="Proven Technology" 
                variant="outlined" 
                sx={{ mb: 1 }}
              />
            </Stack>
          </Box>

          {/* Email CTA */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Paper
              sx={{
                p: 4,
                maxWidth: 600,
                mx: 'auto',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
              }}
            >
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold', mb: 3 }}>
                Ready to Get Started?
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4 }}>
                Contact us for enrollment details and take the first step toward deploying 
                intelligent edge AI at your location. Beta testers purchase their own hardware 
                and receive early access to our software platform.
              </Typography>
              <Button
                variant="contained"
                size="large"
                startIcon={<Email />}
                onClick={handleEmailContact}
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold'
                }}
              >
                Email info@pomsoft.net
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Click to open your email client with a pre-filled message
              </Typography>
            </Paper>
          </Box>
        </Container>
      </Box>

      {/* What You'll Get Section */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 6 }}>
          What Beta Testers Get
        </Typography>
        <Grid container spacing={4}>
          {betaTestFeatures.map((feature, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'transform 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: theme.shadows[8]
                  }
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        mr: 2
                      }}
                    >
                      {React.cloneElement(feature.icon, { 
                        sx: { fontSize: 24, color: theme.palette.primary.main } 
                      })}
                    </Box>
                    <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                      {feature.title}
                    </Typography>
                  </Box>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* Current Capabilities */}
      <Box sx={{ bgcolor: alpha(theme.palette.grey[100], 0.5), py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 6 }}>
            Current & Future Capabilities
          </Typography>
          <Grid container spacing={4}>
            {provenCapabilities.map((capability, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Paper
                  sx={{
                    p: 3,
                    height: '100%',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[6]
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {capability.icon}
                    <Box sx={{ ml: 2, flexGrow: 1 }}>
                      <Typography variant="h6" component="h3" sx={{ fontWeight: 'bold' }}>
                        {capability.category}
                      </Typography>
                      <Typography variant="caption" sx={{ 
                        color: capability.status.includes('Validated') ? theme.palette.success.main : theme.palette.warning.main,
                        fontWeight: 'medium'
                      }}>
                        {capability.status}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {capability.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Hardware Requirements */}
      <Box sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05), py: 6 }}>
        <Container maxWidth="lg">
          <Paper
            sx={{
              p: 4,
              border: `2px solid ${alpha(theme.palette.warning.main, 0.3)}`,
              bgcolor: alpha(theme.palette.warning.main, 0.02)
            }}
          >
            <Typography variant="h5" component="h3" gutterBottom sx={{ fontWeight: 'bold', color: theme.palette.warning.main }}>
              ⚠️ Hardware Requirements for Beta Testing
            </Typography>
            <Typography variant="body1" color="text.secondary" paragraph>
              <strong>You buy and install the computer.</strong> EyeSense does not ship hardware.
              The included YOLO deer model does not need a GPU. If you are not running a large vision model, that model fits a smaller computer, including a Raspberry Pi. The factory USB image is larger: 16 GB of RAM and 80 GB of disk.
            </Typography>
            <Grid container spacing={3} sx={{ mt: 2 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Enough for deer detection
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Memory sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Smaller computer, no GPU"
                      secondary="Raspberry Pi class. Runs the included YOLO deer model when you are not using a large vision model."
                    />
                  </ListItem>
                </List>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Optional, only with a GPU
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Memory sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Jetson, Mac Mini, or a GPU PC"
                      secondary="24 GB RAM and 128 GB disk. For large-vision checks or fine-tuning. About $800–$900 with the recommended camera. You can move the trained small model back to the smaller computer."
                    />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CameraAlt sx={{ fontSize: 18, color: theme.palette.secondary.main }} />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Amcrest IP4M-T1044EW-AI"
                      secondary="About $299. Camera is separate from the gateway computer."
                    />
                  </ListItem>
                </List>
              </Grid>
            </Grid>
          </Paper>
        </Container>
      </Box>

      {/* Ideal Candidates */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ mb: 6 }}>
          Who We're Looking For
        </Typography>
        <Paper sx={{ p: 4, maxWidth: 800, mx: 'auto' }}>
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 3 }}>
            We're seeking passionate early adopters who can help us validate our technology 
            in real-world scenarios. Ideal beta testers include:
          </Typography>
          <List>
            {idealCandidates.map((candidate, index) => (
              <ListItem key={index} sx={{ py: 1 }}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <CheckCircle sx={{ fontSize: 20, color: theme.palette.primary.main }} />
                </ListItemIcon>
                <ListItemText primary={candidate} />
              </ListItem>
            ))}
          </List>
        </Paper>
      </Container>

      {/* Technology Preview */}
      <Box sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05), py: 8 }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
                Proven in Real Deployments
              </Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                Our system has been successfully tested in real-world scenarios, demonstrating 
                effective deer and cat deterrence with timely response. Beta testers 
                will have access to this proven technology plus early access to new capabilities.
              </Typography>
              <Box sx={{ mt: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
                  Key Technical Highlights:
                </Typography>
                <List dense>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText primary="Deer detection with no GPU; optional Jetson, Mac, or GPU PC for checks and fine-tuning" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText primary="Amcrest dual-lens 4MP AI camera integration" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText primary="Rachio & Orbit B-hyve smart irrigation control" />
                  </ListItem>
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <CheckCircle sx={{ fontSize: 18, color: theme.palette.success.main }} />
                    </ListItemIcon>
                    <ListItemText primary="WireGuard VPN with zero-trust security" />
                  </ListItem>
                </List>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h5" 
                  component="h3" 
                  gutterBottom 
                  sx={{ 
                    fontWeight: 'bold',
                    mb: 3,
                    color: theme.palette.primary.main
                  }}
                >
                  🎥 See the System in Action
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ mb: 3, fontStyle: 'italic' }}
                >
                  Real deployment footage showing successful deer deterrence
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    height: 300,
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: theme.shadows[8],
                    bgcolor: '#000'
                  }}
                >
                  <iframe
                    width="100%"
                    height="100%"
                    src="https://www.youtube.com/embed/e8pcbWcgYYA"
                    title="EyeSense.ai Deer Deterrent System in Action"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    style={{
                      borderRadius: '8px'
                    }}
                  />
                </Box>
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ mt: 2, display: 'block', fontWeight: 'medium' }}
                >
                  ✅ Real deployment • ✅ Live detection • ✅ Automatic response
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Final CTA */}
      <Box
        sx={{
          bgcolor: theme.palette.primary.main,
          color: 'white',
          py: 8,
          textAlign: 'center'
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Join the Beta Program Today
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: 4, opacity: 0.9 }}>
            Limited spots available for our early beta testing program. 
            Beta testers provide their own hardware and receive early access to our revolutionary software platform.
          </Typography>
          <Button
            variant="contained"
            size="large"
            startIcon={<Email />}
            onClick={handleEmailContact}
            sx={{
              bgcolor: 'white',
              color: theme.palette.primary.main,
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: alpha('#ffffff', 0.9)
              }
            }}
          >
            Contact info@pomsoft.net
          </Button>
          <Typography variant="body2" sx={{ mt: 2, opacity: 0.8 }}>
            We'll respond with enrollment details and next steps
          </Typography>
        </Container>
      </Box>
      <PublicFooter />
    </Box>
  );
};

export default BetaTestersPage;
